import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useIntl } from "react-intl";
import { FormattedMessage } from "@learningblocks/shared-components/lib/FormattedMessage";
import { sanitizeConfigOptions } from "@labxchange/util-sanitization";

import { default as i18n } from "@learningblocks/shared-components/lib/i18n";
import { Modal } from "@labxchange/ui-components/lib/Modal";
import { SingleSelectQuestion } from "@labxchange/ui-components/lib/SingleSelectQuestion";
import { useSingleSelect } from "@labxchange/ui-components/lib/SingleSelectQuestion/useSingleSelect";
import { MultiSelectQuestion } from "@labxchange/ui-components/lib/MultiSelectQuestion";
import { TableQuestion } from "@labxchange/ui-components/lib/TableQuestion";

import { LearnMore } from "@learningblocks/interactives/interactive-infographic-components/lib/LearnMore";
import { SliderWidget } from "@learningblocks/interactives/interactive-infographic-components/lib/SliderWidget";
import { textBodyWidgetAlignmentClass } from "@learningblocks/interactives/interactive-infographic-components/lib/TextBody/utils";

import {
  UNANSWERED_STATE,
  getStateForSubmittedOption,
} from "@learningblocks/interactives/interactive-infographic-components/lib/utils/questionState";

import className from "../style.module.scss";

/**
 * Announces why a widget the learner did not open has appeared.
 *
 * A live region INSIDE the dialog, because the two simpler placements do not
 * survive contact with a real screen reader:
 *
 * - Outside the dialog: the dialog is `aria-modal`, so screen readers confine
 *   themselves to its subtree and never see the update. Verified with
 *   VoiceOver — the text was present, on time and not hidden, and silent.
 * - The dialog's `aria-describedby`: VoiceOver announces the element focus lands
 *   on (the Close button) and summarised the dialog contents instead of reading
 *   its description. Also verified silent.
 *
 * An in-dialog live region is the pattern the questions rendered in this same
 * dialog already rely on for "Attempts left" and answer feedback.
 *
 * Mounted EMPTY and filled a beat later on purpose: a live region that already
 * has its text at mount has not mutated, so there is nothing for assistive tech
 * to report. Polite, so it queues behind the dialog's own announcement rather
 * than cutting off the dialog role and name, which the learner also needs.
 */
function AutoTriggerAnnouncement({ text }) {
  const [announcement, setAnnouncement] = useState("");

  useEffect(() => {
    if (!text) return;
    const timer = setTimeout(() => setAnnouncement(text), 150);
    return () => clearTimeout(timer);
  }, [text]);

  if (!text) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      aria-atomic="true"
      className={className.visuallyHidden}
      data-testid="auto-trigger-widget-announcement"
    >
      {announcement}
    </div>
  );
}

AutoTriggerAnnouncement.propTypes = {
  text: PropTypes.string,
};

/**
 * Wraps SingleSelectQuestion so the submitted option can be mirrored onto the
 * hotspot as a state, letting scenes depend on an answer the same way they
 * depend on a state-changer.
 *
 * The submitted option is read from the question store rather than tracked here:
 * that store is the same one the question itself renders from, so the state
 * stays correct however the learner got there, including reopening a question
 * they answered earlier.
 */
function SingleSelectWidget({ hotspotID, onQuestionState, ...questionProps }) {
  const [{ submittedOptionIndexes }] = useSingleSelect(hotspotID);

  const hasSubmissions = submittedOptionIndexes.length > 0;
  const lastSubmittedOptionId =
    submittedOptionIndexes[submittedOptionIndexes.length - 1];

  // A scalar keeps this stable across the store's identity changes, so the
  // effect fires on real submissions rather than on every render.
  const submittedState = hasSubmissions
    ? getStateForSubmittedOption(questionProps.options, lastSubmittedOptionId)
    : null;

  // Only a reset the learner performs should send the hotspot back to its
  // unanswered state. An empty store is not by itself a reset: a hotspot can
  // carry a state restored from a persisted scene with no submissions behind it,
  // and reporting UNANSWERED_STATE on mount would wipe that state merely because
  // the learner opened the question.
  const hasReportedSubmissionRef = React.useRef(false);

  React.useEffect(() => {
    if (submittedState !== null) {
      hasReportedSubmissionRef.current = true;
      onQuestionState?.(hotspotID, submittedState);
    } else if (!hasSubmissions && hasReportedSubmissionRef.current) {
      hasReportedSubmissionRef.current = false;
      onQuestionState?.(hotspotID, UNANSWERED_STATE);
    }
  }, [hasSubmissions, submittedState, hotspotID, onQuestionState]);

  return <SingleSelectQuestion {...questionProps} id={hotspotID} />;
}

SingleSelectWidget.propTypes = {
  hotspotID: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  onQuestionState: PropTypes.func,
  options: PropTypes.array,
};

export function WidgetWrapper(props) {
  const intl = useIntl();
  const widgetData = { ...props.widgetProps };

  // Informational widgets (learn-more, text-body) exist to convey text; point the
  // dialog's aria-describedby at it so a screen reader reads it when the modal opens.
  const isInformational =
    props.type === "learn-more" || props.type === "text-body";
  const contentDescribedById = `widget-content-${props.hotspotID}`;
  const dialogAriaLabel = isInformational
    ? intl.formatMessage({
        id: "widget.informationDialogLabel",
        defaultMessage: "Information",
        description: "Accessible name for an informational modal dialog",
      })
    : undefined;
  const totalAttempts =
    widgetData?.totalAttempts < 0 ? -1 : widgetData?.totalAttempts;

  // MultiSelectQuestion renders each `content` token in order but has no
  // "checkboxes" case, so substitute the scene's "checkboxes" token with
  // "options" IN PLACE. This keeps the options in the scene-dictated position
  // (e.g. above a trailing "hintDetails" Hint), matching CSCQ. If the scene
  // omits "checkboxes", append "options" so the options still render.
  const mscqContent = (() => {
    const mapped = (widgetData.content || []).map((c) =>
      c === "checkboxes" ? "options" : c
    );
    return mapped.includes("options") ? mapped : [...mapped, "options"];
  })();
  // CONTENT_BASE_URL may be undefined on some environments (e.g. stage servers).
  // Fall back to location.pathname so image URLs are resolved relative to the
  // simulation's own path rather than producing a literal "undefined" prefix.
  const baseUrl = window.CONTENT_BASE_URL ?? location.pathname;

  const imageObj = widgetData.image
    ? {
        src: props.preview
          ? widgetData.image.url
          : `${baseUrl}locales/${i18n.language}/images/${widgetData.image.src}`,
        altText: widgetData.image.altText
          ? widgetData.image.altText
          : "Question illustration",
        containerBgColor: "#003e6b",
        ...(widgetData.image.caption && {
          caption: widgetData.image.caption,
        }),
      }
    : undefined;

  const closeWidget = () => {
    if (props.type === "learn-more" || props.type === "Slider") {
      props.handleTrigger(props.hotspotID);
    } else if (props.widgetProps.complete) {
      props.handleTrigger(props.hotspotID);
    }
    props.handleCloseWidget();
  };

  return (
    <Modal
      onClose={closeWidget}
      ariaLabel={dialogAriaLabel}
      ariaDescribedBy={isInformational ? contentDescribedById : undefined}
    >
      <AutoTriggerAnnouncement text={props.autoTriggerAnnouncement} />
      <div
        className={className.widget}
        id={isInformational ? contentDescribedById : undefined}
      >
        {props.type === "text-body" ? (
          <div
            className={`${className.textBodyWidget} ${textBodyWidgetAlignmentClass(props.widgetProps.textAlign)}`.trim()}
          >
            <FormattedMessage
              string={props.widgetProps.text}
              sanitizeConfig={sanitizeConfigOptions.BlockText}
              description=""
            />
          </div>
        ) : props.type === "CSCQ" ? (
          <SingleSelectWidget
            {...widgetData}
            hotspotID={props.hotspotID}
            onQuestionState={props.handleQuestionState}
            totalAttempts={totalAttempts}
            setCompleted={(completed) => {
              if (completed) {
                props.handleWidgetComplete(props.hotspotID);
              }
            }}
            onReset={() => props.handleWidgetReset(props.hotspotID)}
            image={imageObj}
          />
        ) : props.type === "MSCQ" ? (
          <MultiSelectQuestion
            {...props.widgetProps}
            id={props.hotspotID}
            content={mscqContent}
            options={widgetData.checkboxes || []}
            totalAttempts={totalAttempts}
            setCompleted={(completed) => {
              if (completed) {
                props.handleWidgetComplete(props.hotspotID);
              }
            }}
            onReset={() => props.handleWidgetReset(props.hotspotID)}
            image={imageObj}
          />
        ) : props.type === "learn-more" ? (
          <LearnMore {...props.widgetProps} preview={props.preview} />
        ) : props.type === "TABLE_QUESTION" ? (
          <TableQuestion
            {...props.widgetProps}
            key={props.hotspotID}
            data={(widgetData.data || []).map((row) =>
              row.map((cell) =>
                cell.type === "INPUT" || cell.type === "DROPDOWN"
                  ? { ...cell, showFeedback: true }
                  : cell
              )
            )}
            title={widgetData.heading || widgetData.title}
            hint={widgetData.hintDetails || widgetData.hint}
            totalAttempts={totalAttempts}
            correct={widgetData.complete}
            submitDisabled={widgetData.complete}
            questionType={widgetData.type}
            content={
              // The legacy simData `content` field was designed for an older component
              // that always rendered the table. The new TableQuestion only renders the
              // table when "table" is present in `content`. Adding this to ensure it
              // is always included.
              (widgetData.content
                ? widgetData.content.includes("table")
                  ? widgetData.content
                  : [...widgetData.content, "table"]
                : ["text", "table"]
              ).map((item) =>
                item === "heading"
                  ? "title"
                  : item === "hintDetails"
                    ? "hint"
                    : item
              )
            }
            setCompleted={(completed) => {
              if (completed) {
                props.handleWidgetComplete(props.hotspotID);
              }
            }}
            onReset={() => props.handleWidgetReset(props.hotspotID)}
            image={imageObj}
          />
        ) : props.type === "Slider" ? (
          <SliderWidget data={props.widgetProps} />
        ) : null}
      </div>
    </Modal>
  );
}

WidgetWrapper.propTypes = {
  closeWidget: PropTypes.bool.isRequired,
  type: PropTypes.string.isRequired,
  widgetProps: PropTypes.object.isRequired,
  handleCloseWidget: PropTypes.func.isRequired,
  preview: PropTypes.bool,
  handleWidgetComplete: PropTypes.func.isRequired,
  handleWidgetReset: PropTypes.func.isRequired,
  autoTriggerAnnouncement: PropTypes.string,
  handleQuestionState: PropTypes.func,
};
WidgetWrapper.defaultProps = {
  preview: false,
};
